import { Box, Button, Container, Text, VStack } from "@chakra-ui/react";
import { Component, ErrorInfo, ReactNode } from "react";
import { toaster } from "./ui/toaster";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class GlobalErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Global Error Boundary caught an error:", error, errorInfo);

    // Show toast notification for the error
    toaster.create({
      title: "Application Error",
      description:
        "Something went wrong. Please refresh the page or try again.",
      type: "error",
      duration: 5000,
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  public render() {
    if (this.state.hasError) {
      return (
        <Container py={8}>
          <VStack gap={6} textAlign="center">
            <Box>
              <Text fontSize="2xl" fontWeight="bold" color="red.500" mb={2}>
                Oops! Something went wrong
              </Text>
              <Text color="gray.600" mb={4}>
                We're sorry, but something unexpected happened. Please try
                refreshing the page.
              </Text>
              {process.env.NODE_ENV === "development" && this.state.error && (
                <Box
                  bg="red.50"
                  p={4}
                  borderRadius="md"
                  textAlign="left"
                  fontSize="sm"
                  fontFamily="mono"
                  color="red.700"
                  maxW="600px"
                  overflow="auto"
                >
                  <Text fontWeight="bold" mb={2}>
                    Error Details:
                  </Text>
                  <Text>{this.state.error.message}</Text>
                  <Text mt={2} fontSize="xs">
                    {this.state.error.stack}
                  </Text>
                </Box>
              )}
            </Box>
            <VStack gap={3}>
              <Button colorPalette="blue" onClick={this.handleReload} size="lg">
                Refresh Page
              </Button>
              <Button variant="outline" onClick={this.handleReset} size="md">
                Try Again
              </Button>
            </VStack>
          </VStack>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default GlobalErrorBoundary;
